<template>
  <article class="point-result" :class="'point-result--' + status.glyph">
    <!-- Header: point label + derived overall verdict ---------------------- -->
    <header class="point-result__top">
      <span class="point-result__label">{{ point.label }}</span>
      <StatusChip :label="status.label" :variant="status.variant" />
    </header>

    <!-- Coordinates. SITE metres. Deliberately a separate block from the
         deviation table below, which is MILLIMETRES — one shared unit label
         over both would be wrong by 1000x. -->
    <p class="point-result__cap">Coordinates · site metres</p>
    <div class="coords">
      <span class="coords__k"></span>
      <span class="coords__h">X</span>
      <span class="coords__h">Y</span>
      <span class="coords__h">Z</span>

      <span class="coords__k">{{ designLabel }}</span>
      <span class="coords__v">{{ designCells.x }}</span>
      <span class="coords__v">{{ designCells.y }}</span>
      <span class="coords__v">{{ designCells.z }}</span>

      <span class="coords__k">{{ asBuiltLabel }}</span>
      <span class="coords__v">{{ asBuiltCells.x }}</span>
      <span class="coords__v">{{ asBuiltCells.y }}</span>
      <span class="coords__v">{{ asBuiltCells.z }}</span>
    </div>

    <!-- Per-axis deviation. MILLIMETRES, 1 dp, signed. Never a combined 3D
         distance. An incomplete point shows an em dash and Pending — never a
         false FAIL. -->
    <p class="point-result__cap">Deviation · millimetres</p>
    <div class="axes">
      <span class="axes__h axes__h--spacer"></span>
      <span class="axes__h">Deviation</span>
      <span class="axes__h">Tolerance</span>
      <span class="axes__h axes__h--end">Result</span>

      <template v-for="row in axisRows">
        <AxisBadge :key="row.axis + '-badge'" :axis="row.axis" :state="row.badgeState" />
        <span
          :key="row.axis + '-delta'"
          class="axes__v"
          :class="row.deltaClass"
        >{{ row.delta }}</span>
        <span :key="row.axis + '-tol'" class="axes__v axes__v--muted">{{ row.tolerance }}</span>
        <StatusChip
          :key="row.axis + '-chip'"
          class="axes__chip"
          :label="row.status.label"
          :variant="row.status.variant"
        />
      </template>
    </div>

    <!-- Provenance. A value with no stated origin is not defensible on a
         conformance report, so this block always renders — including when the
         answer is "not captured". -->
    <footer class="prov">
      <p class="point-result__cap point-result__cap--flush">Provenance</p>

      <div class="prov__row">
        <span class="prov__k">As-built</span>
        <span class="prov__v">{{ asBuiltSourceLabel }}</span>
      </div>
      <div class="prov__row">
        <span class="prov__k">Design</span>
        <span class="prov__v">{{ designSourceLabel }}</span>
      </div>
      <div class="prov__row">
        <span class="prov__k">Scan</span>
        <span class="prov__v">{{ scanLabel }}</span>
      </div>
      <div v-if="scanChoiceLabel" class="prov__row">
        <span class="prov__k">Scan choice</span>
        <span class="prov__v">{{ scanChoiceLabel }}</span>
      </div>
      <div v-if="planesLabel" class="prov__row">
        <span class="prov__k">Views</span>
        <span class="prov__v">{{ planesLabel }}</span>
      </div>
      <div v-if="scaleLabel" class="prov__row">
        <span class="prov__k">Screen scale</span>
        <span class="prov__v" :class="{ 'prov__v--warn': scaleIsCoarse }">{{ scaleLabel }}</span>
      </div>

      <p v-if="scaleIsCoarse" class="prov__warn">
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
          <path
            d="M8 2.2 L14.4 13.4 L1.6 13.4 Z"
            fill="none"
            stroke="currentColor"
            stroke-width="1.2"
            stroke-linejoin="round"
          />
          <line x1="8" y1="6.2" x2="8" y2="9.8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" />
          <circle cx="8" cy="11.6" r="0.8" fill="currentColor" />
        </svg>
        <span>{{ coarseMessage }}</span>
      </p>
    </footer>

    <!-- Evidence. Restores the slot CheckRow dropped, now owned by the point:
         the HPC template carries a system-populated Screenshot column, so an
         image is part of the record rather than a nice-to-have. -->
    <section v-if="evidence.length" class="evidence">
      <p class="point-result__cap">Evidence · {{ evidence.length }}</p>

      <ul class="evidence__strip">
        <li v-for="shot in evidence" :key="shot.id" class="evidence__item">
          <img class="evidence__img" :src="shot.dataUrl" :alt="altFor(shot)" />
          <span class="evidence__meta">{{ shot.viewLabel }} · {{ timeFor(shot) }}</span>
          <button
            type="button"
            class="evidence__remove"
            :title="`Remove this ${shot.viewLabel} capture`"
            :aria-label="`Remove ${altFor(shot)}`"
            @click="$emit('remove-evidence', shot.id)"
          >×</button>
        </li>
      </ul>

      <p v-if="evidenceOverCap" class="evidence__note">
        {{ evidence.length }} images on one point — the report will be large and
        this point's images will span pages. {{ softCap }} is usually plenty.
      </p>
    </section>

    <label class="point-comment">
      <span class="point-comment__label">Point comment</span>
      <textarea
        :value="point.comment || ''"
        rows="2"
        maxlength="1000"
        :placeholder="`Add a comment for ${point.label}`"
        @input="$emit('comment', $event.target.value)"
      ></textarea>
    </label>
  </article>
</template>

<script>
/**
 * Right-panel result card for ONE verification point (AVA-475).
 *
 * Point-centric replacement for the check-centric `CheckRow.vue`. It carries
 * over that component's visual language (card shell, mono key/value grid,
 * axis badge + status chip header) but NOT its data assumptions: there is no
 * manual verdict here. Every verdict on this card is DERIVED by
 * `GET_POINTS_WITH_RESULTS` from the raw points and the tolerance, so the
 * surveyor cannot click a support into conformance. CheckRow's verdict trio,
 * evidence slot and deviation bar are deliberately not carried over.
 *
 * UNITS — the single most dangerous thing on this card. THREE SPACES, not two:
 *   designAdjusted / asBuiltAdjusted (props) -> VIEWER units, whatever the
 *                                               resolved model calls a
 *                                               coordinate: metres on HPC/EDF,
 *                                               MILLIMETRES on Merchant
 *   the coordinate cells this card renders   -> SITE metres (4 dp)
 *   deltas / tolerance                       -> MILLIMETRES (1 dp)
 *
 * THE CARD IS THE THIRD `viewerToSite` LEAF (utils/modelUnits.js header). A
 * coordinate under a metres caption on a conformance surface has to be where the
 * support actually IS: on Merchant the same point reads 25190 in viewer units
 * and 15.6 on site, and both look like coordinates. The conversion happens in a
 * computed over the PROPS and produces new objects — `this.point` is the object
 * the marker projection is drawing from, and converting it would move every
 * marker on the viewer.
 *
 * The site side is deliberately one decimal finer than the deviation: 4 dp of a
 * metre is 0.1 mm, so a position always resolves finer than the deviation
 * derived from it. `computeDeltas` owns the viewer-units -> millimetres step
 * (pointMath.js). The two are rendered in two separate blocks with their own
 * unit caption for exactly that reason.
 *
 * A null transform renders every coordinate as '—'. A blank cell says "not
 * known yet"; an unconverted one would say "the support is 25 km east".
 *
 * TRI-STATE — `pass` / `passX` / `passY` / `passZ` are `true | false | null`.
 * A `pass ? 'PASS' : 'FAIL'` ternary renders a false FAIL on an incomplete
 * point, which the domain rules forbid. Every branch here tests `=== true` /
 * `=== false` explicitly.
 *
 * Pure prop-in leaf: reads no store and dispatches nothing — which is why the
 * unit transform arrives as a PROP from ResultsPanel rather than being mapped
 * here. It emits `comment` with this point's note; ResultsPanel supplies the
 * point id.
 * The parent (ResultsPanel) maps `GET_POINTS_WITH_RESULTS` and renders one
 * card per point. The root carries its own `margin-bottom` so it drops into
 * `.check-list` exactly as CheckRow did — the parent adds no extra spacing.
 */
import AxisBadge from '@/features/Verification/components/shared/AxisBadge.vue';
import StatusChip from '@/features/Verification/components/shared/StatusChip.vue';
import { formatDelta, formatMetres } from '@/features/Verification/utils/pointMath';
import { viewerToSite } from '@/features/Verification/utils/modelUnits';
import { formatTolerance } from '@/features/Verification/utils/checkStatusDisplay';
import { isZoomAdequate } from '@/features/Verification/utils/cloudPick';
import {
  EVIDENCE_SOFT_CAP,
  formatEvidenceTime,
} from '@/features/Verification/utils/evidenceCapture';
import { AS_BUILT_SOURCE } from '@/features/Verification/modules/verification.module';
import { log as debugLog } from '@/utils/debugLogger';

/**
 * Point-model status vocabulary — same labels and the same StatusChip variants
 * as `PointList.vue`, so a point reads identically in the left list and here.
 *
 * `checkStatusDisplay`'s `toStatusChipLabel` / `toStatusChipVariant` are NOT
 * usable: they switch on check-status STRINGS ('Pass' / 'Fail' / 'Pending'),
 * and `toStatusChipLabel(true)` returns an em dash. Only its vocabulary-neutral
 * formatters (`formatTolerance`) are reused.
 *
 * StatusChip has no `pending` variant, so Pending maps to `progress`.
 */
const STATUS_PASS = Object.freeze({ label: 'PASS', variant: 'pass', glyph: 'pass' });
const STATUS_FAIL = Object.freeze({ label: 'FAIL', variant: 'fail', glyph: 'fail' });
const STATUS_PENDING = Object.freeze({ label: 'Pending', variant: 'progress', glyph: 'pending' });

/**
 * A LOCKED AXIS IS NOT PENDING, AND THE DIFFERENCE MATTERS.
 *
 * Both report a `null` verdict, so without this they render identically — and
 * "Pending" tells the reader "not measured YET, come back later" when the truth
 * is "asserted from design, and never will be". On a conformance document that
 * is a materially different claim.
 *
 * This is the client half of the gap recorded as BLOCKER 2 in
 * .claude/features/AVA-475_SPIKE_FINDINGS.md: `checkStatusDisplay.js` has no
 * status meaning "asserted, not measured" (Pass|Fail|Pending|UnableToVerify|
 * NotApplicable), and that traces to the Phase 0 `CheckResultVM` DTO. A LABEL
 * needs no API change and is fixed here; a PERSISTED status still does, and must
 * be settled before this is a real conformance record.
 *
 * StatusChip has no dedicated variant, so it reuses `progress` — deliberately
 * NOT `pass`.
 */
const STATUS_LOCKED = Object.freeze({ label: 'Locked', variant: 'progress', glyph: 'pending' });

/** AxisBadge has no `pending` state — an unassessed axis stays `default`. */
const AXIS_STATE_DEFAULT = 'default';

/** Axis rows, in report order. `delta` names the matching key on `deltas`. */
const AXES = Object.freeze([
  Object.freeze({ axis: 'X', key: 'x', delta: 'dX', pass: 'passX' }),
  Object.freeze({ axis: 'Y', key: 'y', delta: 'dY', pass: 'passY' }),
  Object.freeze({ axis: 'Z', key: 'z', delta: 'dZ', pass: 'passZ' }),
]);

/** How the reading itself was obtained (`asBuiltSource` / `designSource`). */
const SOURCE_LABELS = Object.freeze({
  [AS_BUILT_SOURCE.CLOUD_PICK]: 'Cloud pick',
  [AS_BUILT_SOURCE.MANUAL]: 'Manual entry',
  modelPick: 'Model pick',
});

const EM_DASH = '—';
const SCALE_DECIMALS = 2;

/**
 * Four-way verdict mapping. Never a ternary — `null` must not read as FAIL.
 *
 * `locked` splits the two distinct reasons a verdict is `null`: not measured
 * yet, versus never going to be. See STATUS_LOCKED.
 */
function statusFor(flag, locked = false) {
  if (flag === true) return STATUS_PASS;
  if (flag === false) return STATUS_FAIL;
  return locked ? STATUS_LOCKED : STATUS_PENDING;
}

function axisStateFor(flag) {
  if (flag === true) return 'pass';
  if (flag === false) return 'fail';
  return AXIS_STATE_DEFAULT;
}

/** True only for an offset that actually shifts the point. */
function hasOffset(offset) {
  return !!offset && (offset.x !== 0 || offset.y !== 0 || offset.z !== 0);
}

/**
 * The three coordinate cells for one point, from a SITE-metre point.
 *
 * Stays a fixed 4 dp — `POSITION_DECIMALS`, via `formatMetres`. Its input is
 * metres on every facility because `viewerToSite` has already been applied, so
 * the unit-aware precision the wizard needs for raw viewer units would be wrong
 * here: it would render a millimetre facility's site coordinates at 1 dp.
 *
 * DOM call site, so NO `ascii` — the shared formatter's default is U+2212 MINUS
 * SIGN, which is what `formatDelta` renders in the deviation block directly
 * below. An ASCII hyphen here would make the two blocks' signs disagree in both
 * glyph and width. The PDF is the opposite case and passes `ascii: true`; see
 * `formatMetres` in pointMath.js.
 *
 * NOT `signed` — these are positions, not changes.
 */
function toCells(point) {
  return {
    x: formatMetres(point ? point.x : null),
    y: formatMetres(point ? point.y : null),
    z: formatMetres(point ? point.z : null),
  };
}

export default {
  name: 'PointResultCard',
  components: { AxisBadge, StatusChip },
  props: {
    /**
     * One decorated entry from `VERIFICATION/GET_POINTS_WITH_RESULTS`:
     * { id, label, comment, design, designSource, designOffset, asBuilt, asBuiltSource,
     *   asBuiltOffset, asBuiltScanName, asBuiltScanNames, asBuiltScanIsManual,
     *   asBuiltMmPerPx, asBuiltPlanes, tolerance, designAdjusted,
     *   asBuiltAdjusted, deltas, passX, passY, passZ, pass, isComplete,
     *   showOffsetCheck }
     */
    point: {
      type: Object,
      required: true,
    },

    /**
     * `{ mmPerViewerUnit, siteScale, siteOffset }` from `GET_UNIT_TRANSFORM`,
     * threaded by ResultsPanel. Null until the pre-flight resolves the model,
     * and then the coordinate cells read '—' rather than guessing a scale.
     */
    unitTransform: {
      type: Object,
      default: null,
    },
  },
  computed: {
    status() {
      return statusFor(this.point.pass);
    },

    tolerance() {
      return this.point.tolerance || null;
    },

    /**
     * Coordinates shown are the ADJUSTED ones — the values the deltas use —
     * converted to SITE metres for display. `viewerToSite` returns a NEW object
     * and nothing keeps it: `this.point` belongs to the store's getter cache.
     */
    designCells() {
      return toCells(viewerToSite(this.point.designAdjusted, this.unitTransform));
    },

    asBuiltCells() {
      return toCells(viewerToSite(this.point.asBuiltAdjusted, this.unitTransform));
    },

    designLabel() {
      return hasOffset(this.point.designOffset) ? 'Design +off' : 'Design';
    },

    asBuiltLabel() {
      return hasOffset(this.point.asBuiltOffset) ? 'As-built +off' : 'As-built';
    },

    axisRows() {
      const { deltas } = this.point;
      const tol = this.tolerance;
      const locked = this.point.lockedAxes || [];
      return AXES.map((a) => {
        const flag = this.point[a.pass];
        const isLocked = locked.includes(a.key);
        return {
          axis: a.axis,
          // A locked axis's deviation is zero BY CONSTRUCTION — it is the design
          // value compared against itself. Printing "+0.0" invites it to be read
          // as a measurement that came out perfect, which is the single most
          // flattering misreading available. Show nothing instead.
          delta: isLocked ? EM_DASH : formatDelta(deltas ? deltas[a.delta] : null),
          deltaClass: this.deltaClassFor(flag),
          tolerance: tol ? formatTolerance(tol[a.key], 'mm') : EM_DASH,
          status: statusFor(flag, isLocked),
          badgeState: axisStateFor(flag),
        };
      });
    },

    // ---- Provenance -------------------------------------------------------

    asBuiltSourceLabel() {
      const label = SOURCE_LABELS[this.point.asBuiltSource];
      return label || `${EM_DASH} not captured`;
    },

    designSourceLabel() {
      const label = SOURCE_LABELS[this.point.designSource];
      return label || `${EM_DASH} not set`;
    },

    /** Every cloud that contributed. Falsy scan name + several names = spanned. */
    scanNames() {
      const names = this.point.asBuiltScanNames;
      return Array.isArray(names) ? names.filter(Boolean) : [];
    },

    scanLabel() {
      if (this.point.asBuiltScanName) return this.point.asBuiltScanName;
      if (this.scanNames.length) return this.scanNames.join(' + ');
      return EM_DASH;
    },

    /**
     * How the CLOUD was chosen — an axis independent of how the READING was
     * taken. Hidden when no scan is recorded (a manual entry has no cloud).
     */
    scanChoiceLabel() {
      if (this.scanLabel === EM_DASH) return null;
      return this.point.asBuiltScanIsManual ? 'Chosen by surveyor' : 'Automatic';
    },

    planesLabel() {
      const planes = this.point.asBuiltPlanes;
      if (!Array.isArray(planes) || !planes.length) return null;
      return planes.join(' + ');
    },

    /** Worst-axis screen scale recorded at capture, in mm per pixel. */
    mmPerPx() {
      const value = this.point.asBuiltMmPerPx;
      return typeof value === 'number' && Number.isFinite(value) ? value : null;
    },

    /**
     * Absent is not the same as inadequate. A manual entry never had a screen
     * scale, so the row is hidden rather than reported as unmeasured.
     */
    scaleLabel() {
      if (this.mmPerPx !== null) {
        const scale = `${this.mmPerPx.toFixed(SCALE_DECIMALS)} mm/px`;
        // Without a tolerance the scale cannot be judged adequate OR coarse.
        // Say so rather than rendering it plain, which would read as "fine".
        return this.tightestToleranceMm === null ? `${scale} · tolerance not set` : scale;
      }
      if (this.point.asBuiltSource === AS_BUILT_SOURCE.CLOUD_PICK) return 'not recorded';
      return null;
    },

    /** Tightest axis tolerance — the pixel must serve the hardest one. */
    tightestToleranceMm() {
      const tol = this.tolerance;
      if (!tol) return null;
      const values = [tol.x, tol.y, tol.z].filter((v) => typeof v === 'number' && v > 0);
      return values.length ? Math.min(...values) : null;
    },

    /**
     * `isZoomAdequate` takes a `{ horiz, vert }` screen-scale pair and
     * reduces it to the worst axis. `asBuiltMmPerPx` is ALREADY that worst
     * value, so feeding it as both axes is mathematically identical — and it
     * keeps the tolerance/pixel ratio defined in exactly one place
     * (cloudPick.js `TOLERANCE_PIXEL_RATIO`, which is not exported).
     */
    scaleIsCoarse() {
      if (this.mmPerPx === null || this.tightestToleranceMm === null) return false;
      return !isZoomAdequate(
        { horiz: this.mmPerPx, vert: this.mmPerPx },
        this.tightestToleranceMm,
      );
    },

    // ---- Evidence ---------------------------------------------------------

    /** Normalized upstream by GET_POINTS_WITH_RESULTS; guarded for safety. */
    evidence() {
      return this.point.evidence || [];
    },

    softCap() {
      return EVIDENCE_SOFT_CAP;
    },

    evidenceOverCap() {
      return this.evidence.length > EVIDENCE_SOFT_CAP;
    },

    coarseMessage() {
      const scale = this.mmPerPx === null ? EM_DASH : this.mmPerPx.toFixed(SCALE_DECIMALS);
      // Tolerance string comes from `formatTolerance` so the U+00B1 glyph has
      // exactly one source on this card.
      const tol = formatTolerance(this.tightestToleranceMm, 'mm');
      return `Captured at ${scale} mm/px — too coarse to assess ${tol}. Re-capture zoomed in.`;
    },
  },
  mounted() {
    // Logged once per card mount, not in a computed — a computed would re-log
    // on every tolerance edit.
    debugLog('[PointResultCard] rendered', {
      label: this.point.label,
      pass: this.point.pass,
      asBuiltSource: this.point.asBuiltSource,
      scan: this.scanLabel,
      mmPerPx: this.mmPerPx,
      scaleIsCoarse: this.scaleIsCoarse,
    });
  },
  methods: {
    deltaClassFor(flag) {
      if (flag === true) return 'axes__v--pass';
      if (flag === false) return 'axes__v--fail';
      return null;
    },

    timeFor(shot) {
      return formatEvidenceTime(shot.capturedAt);
    },

    /**
     * The thumbnail is not decoration — it is the record of what the surveyor
     * was looking at. Screen-reader users get the same three facts the sighted
     * caption carries: which point, which view, and when.
     */
    altFor(shot) {
      return `Evidence for ${this.point.label}, ${shot.viewLabel} view, captured ${this.timeFor(shot)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

// Card shell carried over from CheckRow so both read as one family. No tinted
// wash and no coloured edge bar in any state — status is carried by the chips,
// the axis badges and the deviation text colour.
.point-result {
  padding: 12px;
  border: 1px solid $vf-line-soft;
  border-radius: $vf-r-md;
  margin-bottom: 8px;
  background: $vf-panel-2;

  &--fail {
    border-color: rgba(183, 28, 28, 0.35);
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;
  }

  &__label {
    font-family: $vf-font-mono;
    font-size: 13.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: $vf-ink;
  }

  // Unit caption above each numeric block. The two blocks are in DIFFERENT
  // units; the caption is the only thing stopping a reader assuming otherwise.
  &__cap {
    margin: 10px 0 5px;
    font-family: $vf-font-mono;
    font-size: 9.5px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $vf-ink-3;

    &--flush {
      margin-top: 0;
    }
  }
}

// ---- Coordinates (metres) -------------------------------------------------
.coords {
  display: grid;
  grid-template-columns: auto repeat(3, minmax(0, 1fr));
  gap: 3px 8px;
  align-items: baseline;

  &__h {
    font-family: $vf-font-mono;
    font-size: 9.5px;
    letter-spacing: 0.08em;
    color: $vf-ink-3;
    text-align: right;
  }

  &__k {
    font-family: $vf-font-mono;
    font-size: 10.5px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $vf-ink-2;
    white-space: nowrap;
  }

  &__v {
    font-family: $vf-font-mono;
    font-size: 11.5px;
    font-weight: 600;
    color: $vf-ink;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
}

// ---- Per-axis deviation (millimetres) -------------------------------------
.axes {
  display: grid;
  // badge | deviation | tolerance | chip
  grid-template-columns: 20px minmax(0, 1fr) auto auto;
  gap: 5px 8px;
  align-items: center;

  &__h {
    font-family: $vf-font-mono;
    font-size: 9.5px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $vf-ink-3;
    text-align: right;

    &--spacer {
      // Empty cell above the axis badges.
      min-height: 1px;
    }

    &--end {
      text-align: right;
    }
  }

  &__v {
    font-family: $vf-font-mono;
    font-size: 11.5px;
    font-weight: 600;
    color: $vf-ink;
    text-align: right;
    font-variant-numeric: tabular-nums;

    &--muted {
      font-weight: 500;
      color: $vf-ink-2;
      white-space: nowrap;
    }

    &--pass {
      color: $vf-harvest;
    }

    &--fail {
      color: $vf-error;
    }
  }

  &__chip {
    justify-self: end;
  }
}

// ---- Provenance -----------------------------------------------------------
.prov {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed $vf-line-soft;

  &__row {
    display: grid;
    grid-template-columns: 78px minmax(0, 1fr);
    gap: 8px;
    align-items: baseline;
    margin-bottom: 3px;
  }

  &__k {
    font-family: $vf-font-mono;
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $vf-ink-3;
  }

  &__v {
    font-family: $vf-font-mono;
    font-size: 11px;
    color: $vf-ink-2;
    overflow-wrap: anywhere;

    &--warn {
      color: $vf-error;
      font-weight: 600;
    }
  }

  &__warn {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 6px 0 0;
    font-family: $vf-font-mono;
    font-size: 10.5px;
    line-height: 1.35;
    color: $vf-error;

    svg {
      flex-shrink: 0;
      margin-top: 1px;
    }
  }
}

// ---- Evidence thumbnails --------------------------------------------------
.evidence {
  margin-top: 12px;
  padding-top: 2px;
  border-top: 1px dashed $vf-line-soft;

  &__strip {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__item {
    position: relative;
    border: 1px solid $vf-line;
    border-radius: $vf-r-sm;
    overflow: hidden;
    background: #fff;
  }

  // No fixed height and no object-fit: the image's own intrinsic ratio locks
  // the box, so a portrait viewer window is never cropped to look like a
  // landscape one. What is stored is what is shown.
  &__img {
    display: block;
    width: 100%;
    height: auto;
  }

  &__meta {
    display: block;
    padding: 3px 5px;
    border-top: 1px solid $vf-line-soft;
    font-family: $vf-font-mono;
    font-size: 9.5px;
    letter-spacing: 0.04em;
    color: $vf-ink-3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__remove {
    position: absolute;
    top: 3px;
    right: 3px;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    // Sits ON the image, so it needs its own contrast rather than a token —
    // the pixels underneath can be any colour.
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 13px;
    line-height: 1;
    cursor: pointer;

    &:hover {
      background: $vf-error;
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 1px;
    }
  }

  &__note {
    margin: 6px 0 0;
    font-family: $vf-font-mono;
    font-size: 10px;
    line-height: 1.35;
    color: $vf-ink-3;
  }
}

.point-comment {
  display: block;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed $vf-line-soft;

  &__label {
    display: block;
    margin-bottom: 5px;
    font-family: $vf-font-mono;
    font-size: 9.5px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $vf-ink-3;
  }

  textarea {
    display: block;
    width: 100%;
    min-height: 56px;
    max-height: 120px;
    resize: vertical;
    padding: 8px 9px;
    border: 1px solid $vf-line;
    border-radius: $vf-r-sm;
    outline: none;
    background: #fff;
    color: $vf-ink;
    font-family: $vf-font-body;
    font-size: 12px;
    line-height: 1.4;

    &:focus {
      border-color: $vf-primary;
      box-shadow: 0 0 0 2px rgba(3, 98, 76, 0.12);
    }

    &::placeholder {
      color: $vf-ink-3;
    }
  }
}
</style>
